import { DatePickerView } from '@mui/lab/DatePicker/shared';
import { DateTime } from 'luxon';
import { ForeignStateVM } from 'shell/components/shared/foreign-state-list';
import { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import { BaseTableRows } from 'shell/components/shared/table/types';
import { DatiSoggettoCertificazioneList, SubjectData } from 'shell/models/person';
import { ItemVM } from 'shell/view-models/item';
import { PersonActVM } from 'shell/view-models/person-act';
import { ModalityPaymentVM } from 'shell/view-models/modality-payment';
import { CertificateTypeANPRList } from 'shell/models/certificate-type';
import { UploadForm } from './file-extended';
import { PersonVM } from 'shell/view-models/search-person';

export type ListTableItemVM = ItemVM & BaseTableRows & { position?: number; action?: string };

export interface CommonCivilStatusFormVM {
  year?: string;
  registerNumber?: string;
  documentNumber?: string;
  portion?: string;
  serie?: string;
  serie2?: string;
  annotationNational?: string;
  noteNational?: string;
  annotationInternational?: string;
  noteInternational?: string;
  uploadFile?: { file?: File; raw: Array<string>; rawTiff: string };
  annotationNationalList?: Array<ListTableItemVM>;
  annotationInternationalList?: Array<ListTableItemVM>;
  indicatorUpdate?: string;
}

export interface DocumentBirthFormVM extends CommonCivilStatusFormVM {
  matricola?: string;
  surname?: string;
  name?: string;
  gender?: string;
  municipalityBirth?: string;
  surnameFather?: string;
  nameFather?: string;
  surnameMother?: string;
  nameMother?: string;
  time?: DateTime | null;
  birthday?: DateTime | null;
}

export interface DocumentBirthUploadFormVM extends DocumentBirthFormVM {
  upload?: UploadForm;
}

export enum CertificateType {
  nascita = '1',
  matrimonio = '3',
  morte = '2',
  unioneCivile = '4',
  cittadinanza = '5',
}

export interface CertificateValue {
  copia?: string;
  certificato?: string;
  estratto?: string;
  estrattoVienna?: string;
  estrattoParigi?: string;
}

export const CertificateValueBorn: CertificateValue = {
  copia: '01',
  certificato: '02',
  estratto: '03',
  estrattoVienna: '04',
  estrattoParigi: '05',
};

export const CertificateValueMarriage: CertificateValue = {
  copia: '11',
  certificato: '12',
  estratto: '13',
  estrattoVienna: '14',
  estrattoParigi: '15',
};

export const CertificateValueDeath: CertificateValue = {
  copia: '21',
  certificato: '22',
  estratto: '23',
  estrattoVienna: '24',
  estrattoParigi: '25',
};

export const CertificateValueCivilUnion: CertificateValue = {
  copia: '31',
  certificato: '32',
  estratto: '33',
};

export const CertificateValueCitizenship: CertificateValue = {
  copia: '41',
};

export type CertificateRadio = {
  type?: CertificateType;
  value?: string;
  element?: CertificateTypeANPRList;
  id?: number;
};

export type ApplicantFormVM = {
  fiscalCode?: string;
  searchApplicant?: boolean;
  matricola?: string;
  surname?: string;
  withoutSurname?: boolean;
  name?: string;
  withoutName?: boolean;
  gender?: string;
  birthDate?: DateTime | null;
  birthChoiceDate?: Array<DatePickerView>;
  municipality?: MunicipalityAnprVM | null;
  municipalityDocument?: MunicipalityAnprVM | null;
  isForeign?: boolean;
  foreignState?: ForeignStateVM | null;
  foreignMunicipality?: MunicipalityAnprVM | null;
  foreignStateDocument?: string;
  documentNumber?: string;
  documentIssuedDate?: DateTime | null;
  isForeignDocument?: boolean;
  birthplace?: string;
  residence?: string;
  state?: string;
  idSubjectAnpr?: string;
  datiSoggetto?: SubjectData;
  dataPersons?: Array<PersonVM>; // per la ricerca
};

export type AccountholderFormVM = {
  searchAccountholder?: boolean;
  sameAsApplicant?: boolean;
  reasonRelease?: string;
  fiscalCode?: string;
  surname?: string;
  withoutSurname?: boolean;
  name?: string;
  withoutName?: boolean;
  gender?: string;
  birthDate?: DateTime | null;
  birthChoiceDate?: Array<DatePickerView>;
  idTypeDate?: string;
  typeDate?: string;
  age?: string;
  birthplace?: string;
  residence?: string;
  state?: string;
  isForeign?: boolean;
  foreignState?: ForeignStateVM | null;
  foreignMunicipality?: MunicipalityAnprVM | null;
  municipality?: MunicipalityAnprVM | null;
  idNational?: string;
  idSubjectAnpr?: string;
  idAnsc?: string;
  datiSoggetto?: SubjectData;
  datiSoggettoCertificazione?: DatiSoggettoCertificazioneList;
  isANSC?: boolean;
};

export interface CertificationFormVM {
  applicant?: ApplicantFormVM;
  accountholder?: AccountholderFormVM;
  certificate?: CertificateRadio | null;
  chosenDocument?: PersonActVM | null;
  documentChosenBirth?: DocumentBirthFormVM | null;
  documentChosenDeath?: DocumentDeathFormVM | null;
  documentChosenMarriage?: DocumentMarriageFormVM | null;
  documentChosenCivilUnion?: DocumentCivilUnionFormVM | null;
  documentChosenCitizenship?: DocumentCitizenshipFormVM | null;
  file?: File;
  raw?: Array<string>;
  rawTiff?: string;
  timber?: string;
  timberFreeText?: string;
  modifierCode?: string;
  copies: string;
  typeUse?: string;
  requiredTypeUse?: boolean;
  flagUsePublication?: boolean;
  flagCarta?: boolean;
  flagInternal?: boolean;
  flagSign?: boolean;
  flagOmittedSign?: boolean;
  flagPaternita?: boolean;
  flagFincatura?: boolean;
  reason?: string;
  selectedCertificate?: string;
  typePayment?: string;
  releaseMode?: ModalityPaymentVM | null;
  total?: number;
  sigla?: string;
  annunciazioniAtto?: string;
  user?: string;
  pin?: string;
  otp?: string;
}

export interface DocumentMarriageFormVM extends CommonCivilStatusFormVM {
  marriageDate?: DateTime | null;
  marriageMunicipality?: string;
  matricolaHusband?: string;
  surnameHusband?: string;
  nameHusband?: string;
  genderHusband?: string;
  birthdayHusband?: DateTime | null;
  citizenshipWife?: string;
  citizenshipHusband?: string;
  yearsHusband?: string;
  yearDateHusband?: string;
  municipalityBirthHusband?: string;
  residenceMunicipalityHusband?: string;
  statusHusband?: string;
  surnameAfterMarriageHusband?: string;
  matricolaWife?: string;
  surnameWife?: string;
  nameWife?: string;
  genderWife?: string;
  birthdayWife?: DateTime | null;
  yearsWife?: string;
  yearDateWife?: string;
  municipalityBirthWife?: string;
  residenceMunicipalityWife?: string;
  statusWife?: string;
  surnameAfterMarriageWife?: string;
}

export interface DocumentMarriageUploadFormVM extends DocumentMarriageFormVM {
  upload?: UploadForm;
}

export interface DocumentDeathFormVM extends CommonCivilStatusFormVM {
  matricola?: string;
  surname?: string;
  name?: string;
  gender?: string;
  deathPresumed?: boolean;
  hourDeathUnknown?: boolean;
  years?: string;
  yearDate?: string;
  deathDate?: DateTime | null;
  hourDeathDate?: DateTime | null;
  deathBy?: DateTime | null;
  deathTo?: DateTime | null;
  deathMunicipality?: string;
  residenceMunicipality?: string;
  birthday?: DateTime | null;
  municipalityBirth?: string;
  status?: string;
  // surnameFather?: string;
  // nameFather?: string;
  // surnameMother?: string;
  // nameMother?: string;
  spouseSurname?: string;
  spouseName?: string;
}

export interface DocumentDeathUploadFormVM extends DocumentDeathFormVM {
  upload?: UploadForm;
}

export interface DocumentCivilUnionFormVM extends CommonCivilStatusFormVM {
  matricola?: string;
  matricola2?: string;
  surname?: string;
  surname2?: string;
  name?: string;
  yearsDeclarant1?: string;
  yearDateDeclarant1?: string;
  yearDateDeclarant2?: string;
  yearsDeclarant2?: string;
  name2?: string;
  gender?: string;
  gender2?: string;
  municipalityBirth?: string;
  municipalityBirth2?: string;
  time?: DateTime | null;
  time2?: DateTime | null;
  birthday?: DateTime | null;
  birthday2?: DateTime | null;
  nameFather?: string;
  nameFather2?: string;
  surnameFather?: string;
  surnameFather2?: string;
  nameMother?: string;
  nameMother2?: string;
  civilUnionDate?: DateTime | null;
  martitalStatusDeclarant1?: string;
  martitalStatusDeclarant2?: string;
  civilUnionMunicipality?: string;
  surnameMother?: string;
  surnameMother2?: string;
  residenceMunicipalityDeclarant1?: string;
  residenceMunicipalityDeclarant2?: string;
  surnamePostUnionDeclarant1?: string;
  surnamePostUnionDeclarant2?: string;
  citizenshipDeclarant1?: string;
  citizenshipDeclarant2?: string;
}

export interface DocumentCivilUnionUploadFormVM extends DocumentCivilUnionFormVM {
  upload?: UploadForm;
}

export interface DocumentCitizenshipFormVM extends CommonCivilStatusFormVM {
  matricola?: string;
  surname?: string;
  name?: string;
  gender?: string;
  municipalityBirth?: string;
  surnameFather?: string;
  nameFather?: string;
  surnameMother?: string;
  residenceMunicipality?: string;
  citizenshipDate?: DateTime | null;
  nameMother?: string;
  time?: DateTime | null;
  birthday?: DateTime | null;
  birthdayMunicipality?: string;
}
